import type {
  HeadlineResponsePersonBaseRelatedPagedData,
  PlayerOrgRatingAllResponsePagedData,
  PlayerProfileResponse,
  PlayerRatingResponse,
  PlayerVisitsRes,
  PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  PublicMeasurementPersonResponseRecord,
  RecruitmentEvaluationResponsePagedData,
  TeamTargetResponsePagedData,
  VideoResponsePagedData,
} from '@on3/api';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { PlayerSearch } from 'components/Db/Profile/PlayerSearch';
import { Rankings } from 'components/Db/Profile/Rankings';
import { ScoutingJournalModule } from 'components/Db/Profile/ScoutingJournal/ScoutingJournalModule';
import { ProfileVitals } from 'components/Db/Profile/Vitals';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { AdminLink } from 'components/User/AdminLink';
import useAthlete from 'contexts/AthleteProvider';

import { PlayerHeader } from './PlayerHeader';
import { PlayerInterestsModule } from './PlayerInterests/PlayerInterestsModule';
import { PlayerNewsModule } from './PlayerNews/PlayerNewsModule';
import styles from './PlayerRecruiting.module.scss';
import { PlayerVideosModule } from './Videos/PlayerVideosModule';
import { VisitCenter } from './VisitCenter';

interface IPlayerRecruitingProps {
  rankingsAll?: PlayerOrgRatingAllResponsePagedData | null;
  rankingsPlayer?: PlayerRatingResponse | null;
  recruitsArticles?: HeadlineResponsePersonBaseRelatedPagedData | null;
  scoutingJournals?: RecruitmentEvaluationResponsePagedData | null;
  teamTargets?: TeamTargetResponsePagedData | null;
  videoData?: VideoResponsePagedData | null;
  visitCenter?: PlayerVisitsRes | null;
  vitalAverages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData | null;
  vitals?: PublicMeasurementPersonResponseRecord | null;
}

export const PlayerRecruiting = ({
  rankingsAll,
  rankingsPlayer,
  recruitsArticles,
  scoutingJournals,
  teamTargets,
  videoData,
  visitCenter,
  vitalAverages,
  vitals,
}: IPlayerRecruitingProps) => {
  const { athlete } = useAthlete();

  const showRankings = !!rankingsAll;
  const showNews = !!recruitsArticles?.list?.length;
  const showVideos = !!videoData?.list?.length && athlete?.recruitmentKey;
  const showVisits = !!visitCenter?.visits?.length;
  const showVitals = !!vitals?.playerMeasurements?.length && !!vitalAverages;
  const showScoutingJournals = !!scoutingJournals?.list?.length;

  return (
    <DatabaseLayout>
      <div className={styles.block}>
        <AdminLink path={`/people/${athlete?.slug}/edit/`} />

        <section className={styles.playerDetail}>
          <header>
            <h1>{athlete?.name}</h1>
            <PlayerSearch />
          </header>
          <PlayerHeader player={athlete as PlayerProfileResponse} />
        </section>

        <div className={styles.modules}>
          {(showRankings || showVideos) && (
            <div className={styles.top}>
              {showRankings && (
                <Rankings
                  allRankingList={rankingsAll}
                  playerRankings={rankingsPlayer}
                />
              )}

              {showVideos && <PlayerVideosModule videoData={videoData} />}
            </div>
          )}

          {!!teamTargets?.list?.length && (
            <section>
              <h2>Top Teams</h2>
              <PlayerInterestsModule
                list={teamTargets.list.slice(0, 5)}
                recruitmentKey={athlete?.recruitmentKey}
              />
            </section>
          )}

          <MobileMPU id="mobile_mpu_middle" incontent />

          <div className={styles.bottom}>
            {(showNews || showScoutingJournals) && ( // Add more conditions here
              <div>
                {showScoutingJournals && (
                  <div className={styles.scouting}>
                    <h2>Scouting</h2>
                    <ScoutingJournalModule
                      list={scoutingJournals?.list ?? []}
                      pagination={scoutingJournals?.pagination}
                    />
                  </div>
                )}

                {showNews && (
                  <PlayerNewsModule
                    recruitmentKey={athlete?.recruitmentKey}
                    recruitsArticles={recruitsArticles}
                  />
                )}

                {showVitals && (
                  <section className={styles.vitals}>
                    <h2>Vitals</h2>
                    <ProfileVitals
                      averages={vitalAverages}
                      measurements={(vitals.playerMeasurements ?? [])
                        .sort((a, b) => b.key - a.key)
                        ?.filter(
                          (m, i, arr) =>
                            arr.findIndex(
                              (v) =>
                                v.measurementTypeKey === m.measurementTypeKey,
                            ) === i,
                        )
                        ?.slice(0, 6)}
                      player={athlete ?? ({} as PlayerProfileResponse)}
                    />
                  </section>
                )}
              </div>
            )}

            {showVisits && ( // Add more conditions here
              <div>
                {showVisits && (
                  <section className={styles.visits}>
                    <h2>Visit Center</h2>
                    <VisitCenter visitCenter={visitCenter} />
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DatabaseLayout>
  );
};
